import { Box, Stack } from '@mui/joy'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'effector-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Alert } from '~/shared/ui/Alerts'
import { Button } from '~/shared/ui/Buttons'
import { FileExportBlob } from '~/shared/ui/FileExport'
import { UploadIcon } from '~/shared/ui/Icons'
import { Modal, useModal } from '~/shared/ui/Modal'
import { Documents } from '../ImportCarTemplate/Documents'
import { checkIsBlobResponse, subrentPaymentMassImportFx } from './model'

type ImportSubrentPaymentTemplateProps = {
  isShow: boolean
  onCancel: () => void
}

export function ImportSubrentPaymentTemplate({
  isShow,
  onCancel,
}: ImportSubrentPaymentTemplateProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isSubmitting = useStore(subrentPaymentMassImportFx.pending)
  const [file, setFile] = useState<File>()
  const [errorFile, setErrorFile] = useState<Blob>()

  const {
    openModal: errorModalOpen,
    closeModal: errorModalClose,
    isModalShow: isErrorModalShow,
  } = useModal()

  const handleFileSet = (files?: File[]) => {
    setFile(files?.[0])
  }

  const handleCancel = () => {
    setFile(undefined)
    onCancel()
  }

  const handleFileErrorSave = (blob: Blob) => {
    setErrorFile(blob)
    errorModalOpen()
  }

  const handleFileSuccessSave = async () => {
    snackbarEnqueued({
      messageI18n: 'alert.subrent payments are successfully imported',
      variant: 'success',
    })
    await queryClient.invalidateQueries({
      queryKey: ['cars'],
    })
  }

  const handleSave = async () => {
    if (!file) {
      snackbarEnqueued({
        messageI18n: 'alert.upload file for import',
        variant: 'warning',
      })
      return
    }

    try {
      const response = await subrentPaymentMassImportFx(file)

      checkIsBlobResponse(response)
        ? handleFileErrorSave(response.data as Blob)
        : await handleFileSuccessSave()

      handleCancel()
    } catch (e) {
      logger.error(e)
    }
  }

  const handleErrorModalClose = () => {
    errorModalClose()
    setErrorFile(undefined)
  }

  return (
    <>
      <Modal
        isShow={isShow}
        onCancel={isSubmitting ? undefined : handleCancel}
        dialogProps={{
          sx: {
            minWidth: '800px',
            maxWidth: '800px',
          },
        }}
        title={t('subrent payment list upload in progress')}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              '.MuiGrid-direction-xs-row': {
                width: '100%',
              },
            }}
          >
            <Documents
              isShowUploadFiles
              onUploadFiles={handleFileSet}
              isSubmitting={isSubmitting}
              files={file ? [file] : []}
            />
          </Box>

          {isSubmitting && (
            <Alert variant='info'>
              {t(
                'alert.please wait until the import process is complete this may take some time',
              )}
            </Alert>
          )}
          <Stack direction='row' justifyContent='space-between' spacing={2}>
            <Button
              variant='gray'
              fullWidth
              disabled={isSubmitting}
              onClick={onCancel}
            >
              {t('button.close')}
            </Button>
            <Button
              fullWidth
              variant='brand'
              startDecorator={<UploadIcon />}
              onClick={handleSave}
              loading={isSubmitting}
            >
              {t('button.upload subrent payments')}
            </Button>
          </Stack>
        </Stack>
      </Modal>

      <Modal
        isShow={isErrorModalShow}
        onCancel={handleErrorModalClose}
        title={t('import process error')}
      >
        <Stack spacing={2}>
          <Alert variant='error'>
            {t(
              'alert.errors were found in the uploaded import file please fix the errors and try uploading again',
            )}
          </Alert>
          <Stack direction='row' justifyContent='space-between' spacing={2}>
            <Button variant='gray' onClick={handleErrorModalClose}>
              {t('button.close')}
            </Button>
            <FileExportBlob
              titleI18n='button.export import error report'
              fn={() => errorFile}
            />
          </Stack>
        </Stack>
      </Modal>
    </>
  )
}
